import React, { useState, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Badge, Tooltip, theme as antdTheme, Grid, Drawer, Button } from 'antd';
import type { MenuProps } from 'antd';
import { useAppContext } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import {
    UserOutlined,
    MenuOutlined,
    LogoutOutlined,
    ArrowLeftOutlined,
    SettingOutlined,
    ProjectOutlined,
} from '@ant-design/icons';
import { useStageMeta } from '../context/StageMetaContext';
import usePagePermission from '../hooks/usePagePermission';

import lightLogo from '../static/cdrpl.png';
import darkLogo from '../static/cdrpl.png';

const { Sider } = Layout;
const { useBreakpoint } = Grid;

interface SidebarProps {
    collapsible: boolean;
    isMobile?: boolean;
    isSmallDesktop?: boolean;
}

const ExecutionStageNavigation = React.memo(() => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;
    const isSmallDesktop = screens.md && !screens.lg; // 768px to 1200px
    return <Sidebar collapsible={!isMobile} isMobile={isMobile} isSmallDesktop={isSmallDesktop} />;
});

const Sidebar = React.memo(({ collapsible, isMobile, isSmallDesktop }: SidebarProps) => {
    const location = useLocation();
    const { state: stateApp, appContextAction } = useAppContext();
    const { isNavMenuClose } = stateApp;
    const { navMenu } = appContextAction;
    const { theme } = useTheme();
    const { user, logout } = useAuth();
    const { stageMeta } = useStageMeta();
    const isDarkMode = theme.algorithm?.[0] === antdTheme.darkAlgorithm;
    const logoPath = isDarkMode ? darkLogo : lightLogo;

    const isAdminOrIncharge = user?.isadmin || stageMeta?.isIncharge;

    const [drawerVisible, setDrawerVisible] = useState(false);

    const canReadDailyProgress = usePagePermission('daily-progress', 'read');
    const canReadDprReports = usePagePermission('dpr-reports', 'read');
    const canReadDprConfig = usePagePermission('dpr-config', 'read');
    const canReadSettings = usePagePermission('execution-settings', 'read');

    const getThemeStyles = useMemo(() => ({
        sidebarHeader: {
            padding: isNavMenuClose && !isMobile ? '8px' : '0 16px',
            display: 'flex',
            flexDirection: isNavMenuClose && !isMobile ? 'column' as const : 'row' as const,
            alignItems: 'center',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'space-between',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            borderBottom: 'none',
            backgroundColor: 'transparent'
        },
        logoContainer: {
            display: 'flex',
            alignItems: 'center',
            paddingBottom: isNavMenuClose && !isMobile ? '8px' : '0',
            justifyContent: isNavMenuClose && !isMobile ? 'center' : 'flex-start',
            marginBottom: isNavMenuClose && !isMobile ? '8px' : '0'
        },
        logo: {
            width: isNavMenuClose && !isMobile ? '40px' : (isSmallDesktop ? '140px' : '160px'),
            height: 'auto',
            transition: 'width 0.3s ease'
        },
        toggleButton: {
            color: isDarkMode ? '#fff' : '#000',
            background: 'transparent',
            marginTop: isNavMenuClose && !isMobile ? '12px' : undefined
        },
        userEmail: {
            fontSize: '12px',
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'
        },
        collapsedProfile: {
            padding: '16px 0',
            display: 'flex',
            justifyContent: 'center',
            borderBottom: 'none',
            marginBottom: '8px',
            backgroundColor: 'transparent'
        },
        navigationMenu: {
            flex: 1,
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
            padding: '0',
            height: 'auto',
            minHeight: 0
        },
        sidebarStyle: {
            boxShadow: 'none',
            zIndex: 1000,
            height: '100vh',
            position: 'fixed' as const,
            left: 0,
            display: 'flex',
            flexDirection: 'column' as const,
            transition: 'all 0.2s ease',
            background: 'transparent',
            backgroundColor: 'transparent',
            overflow: 'hidden',
            ...(isSmallDesktop && !isNavMenuClose && {
                width: '180px !important'
            })
        }
    }), [isDarkMode, isNavMenuClose, isMobile, isSmallDesktop]);

    const currentPath = useMemo(() => {
        const path = location.pathname;

        if (path === '/execution-stage/settings') return 'settings';
        if (path === '/execution-stage/dpr-config') return 'dpr-config';
        if (path === '/execution-stage/daily-progress') return 'daily-progress';
        if (path === '/execution-stage/dpr-reports') return 'dpr-reports';

        const segments = path.split('/').filter(Boolean);
        return segments.length > 0 ? segments[segments.length - 1] : '';
    }, [location.pathname]);

    const menuItems: MenuProps['items'] = useMemo(() => {
        const items: MenuProps['items'] = [];

        const primaryLinks: MenuProps['items'] = [];

        if (isAdminOrIncharge || canReadDailyProgress) {
            primaryLinks.push({
                key: 'daily-progress',
                icon: <ProjectOutlined />,
                label: <Link to="/execution-stage/daily-progress">Daily Progress</Link>
            });
        }

        if (isAdminOrIncharge || canReadDprReports) {
            primaryLinks.push({
                key: 'dpr-reports',
                icon: <ProjectOutlined />,
                label: <Link to="/execution-stage/dpr-reports">DPR Reports</Link>
            });
        }

        items.push(...primaryLinks);

        if (primaryLinks.length > 0) {
            items.push({ type: 'divider' });
        }

        if (isAdminOrIncharge || canReadDprConfig) {
            items.push({
                key: 'dpr-config',
                icon: <SettingOutlined />,
                label: <Link to="/execution-stage/dpr-config">DPR Config</Link>
            });
        }

        if (isAdminOrIncharge || canReadSettings) {
            items.push({
                key: 'settings',
                icon: <SettingOutlined />,
                label: <Link to="/execution-stage/settings">Settings</Link>
            });
        }

        items.push({ 
            key: 'back-to-stages', 
            icon: <ArrowLeftOutlined />, 
            label: <Link to="/project-selection">Back to Previous</Link> 
        });

        items.push({ 
            key: 'logout', 
            icon: <LogoutOutlined />, 
            label: 'Logout', 
            onClick: logout, 
            danger: true 
        });
        
        return items;
    }, [
        isAdminOrIncharge,
        canReadDailyProgress,
        canReadDprReports,
        canReadDprConfig,
        canReadSettings,
        logout
    ]);

    const toggleCollapse = useCallback(() => {
        if (isNavMenuClose) {
            navMenu.expand();
        } else {
            navMenu.collapse();
        }
    }, [isNavMenuClose, navMenu]);

    const handleMenuClick = useCallback((info: any) => {
        if (info.key === 'logout') {
            logout();
        }

        if (isMobile) {
            setDrawerVisible(false);
        }
    }, [logout, isMobile]);

    const toggleDrawer = useCallback(() => {
        setDrawerVisible(prev => !prev);
    }, []);

    const SidebarContent = useMemo(() => (
        <div className="transparent-nav" style={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden'
        }}>
            <div className="sidebar-header" style={getThemeStyles.sidebarHeader}>
                <div className="logo-container" style={getThemeStyles.logoContainer}>
                    <img
                        src={logoPath}
                        alt="CDRPL"
                        style={getThemeStyles.logo}
                    />
                </div>
            </div>            

            {isNavMenuClose && !isMobile && (
                <div style={getThemeStyles.collapsedProfile}>
                    <Tooltip title={user?.name || 'User'} placement="right">
                        <Badge dot status="success" offset={[-4, 28]}>
                            <Avatar
                                icon={<UserOutlined />}
                                size={36}
                            />
                        </Badge>
                    </Tooltip>
                </div>
            )}

            <div className="navigation-menu" style={getThemeStyles.navigationMenu}>
                <Menu
                    mode="inline"
                    selectedKeys={[currentPath]}
                    defaultOpenKeys={isMobile ? ['main', 'execution', 'settings'] : []}
                    items={menuItems}
                    onClick={handleMenuClick}
                    style={{
                        border: 'none',
                        background: 'transparent',
                        height: '100%'
                    }}
                    theme={isDarkMode ? 'dark' : 'light'}
                />
            </div>
        </div>
    ), [
        getThemeStyles,
        logoPath,
        isNavMenuClose,
        isMobile,
        user,
        currentPath,
        menuItems,
        handleMenuClick,
        isDarkMode
    ]);

    if (isMobile) {
        return (
            <>
                <Button
                    type="primary"
                    shape="circle"
                    icon={<MenuOutlined />}
                    onClick={toggleDrawer}
                    style={{
                        position: 'fixed',
                        top: '16px',
                        left: '16px',
                        zIndex: 999,
                        boxShadow: '0 3px 6px rgba(0,0,0,0.2)'
                    }}
                    size="middle"
                />
                <Drawer
                    placement="left"
                    onClose={toggleDrawer}
                    open={drawerVisible}
                    width={320}
                    styles={{
                        body: {
                            padding: 0,
                            height: '100%',
                            background: 'transparent',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }
                    }}
                    closable={false}
                >
                    {SidebarContent}
                </Drawer>
            </>
        );
    }

    return (
        <Sider
            collapsible
            collapsed={isNavMenuClose}
            onCollapse={toggleCollapse}
            className="navigation-sidebar transparent-nav"
            theme={isDarkMode ? 'dark' : 'light'}
            width={isSmallDesktop ? 180 : 220}
            collapsedWidth={70}
            // Ensure inline style forces transparency over antd default background
            style={getThemeStyles.sidebarStyle}
            trigger={null}
        >
            {SidebarContent}
        </Sider>
    );
});

export default ExecutionStageNavigation;
